/* Cart AJAX carries ?_fd=0 on purpose.

   A store served on its .myshopify.com address forwards requests to the
   primary domain. A POST to /cart/add.js lands in the .myshopify.com cart,
   but the follow-up GET /cart.js is forwarded to the primary domain, which
   keeps a different cart — so the drawer reads an empty cart while the
   customer's items sit in the other one. _fd=0 disables that forwarding, so
   every cart call stays on whichever domain the page was served from. It has
   no effect on the primary domain. */
/* ================================================================
   LAHAZATI MEMORIES — CINEMATIC LUXURY THEME
   app.js v2.0 — Global Application Script
   ================================================================ */

'use strict';

document.addEventListener('DOMContentLoaded', () => {

  /* ================================================================
     1. HEADER — SCROLL GLASSMORPHISM
     ================================================================ */
  const header = document.getElementById('SiteHeader');
  if (header) {
    const handleScroll = () => {
      header.classList.toggle('scrolled', window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // run on init
  }

  /* ================================================================
     2. MOBILE MENU
     ================================================================ */
  const hamburger = document.getElementById('Hamburger');
  const mobileNav = document.getElementById('MobileNav');

  if (hamburger && mobileNav) {
    const panel = mobileNav.querySelector('.mnav__panel');

    const focusables = () => panel.querySelectorAll('a[href], button:not([disabled])');

    const openMenu = () => {
      mobileNav.hidden = false;
      // Force a reflow rather than waiting for a frame: rAF can be throttled
      // (background tab, reduced motion), which would leave the panel stuck
      // off-screen with the menu technically "open".
      void mobileNav.offsetWidth;
      mobileNav.classList.add('is-open');
      hamburger.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
      const first = focusables()[0];
      if (first) first.focus();
    };

    const closeMenu = () => {
      mobileNav.classList.remove('is-open');
      hamburger.setAttribute('aria-expanded', 'false');
      document.body.style.overflow = '';
      // Wait for the slide-out before removing it from the a11y tree.
      setTimeout(() => { if (!mobileNav.classList.contains('is-open')) mobileNav.hidden = true; }, 280);
      // The hamburger is the only thing that opens this, so it is always the
      // right place to send focus back to.
      hamburger.focus();
    };

    hamburger.addEventListener('click', () => {
      mobileNav.classList.contains('is-open') ? closeMenu() : openMenu();
    });

    mobileNav.querySelectorAll('[data-mnav-close]').forEach(el => el.addEventListener('click', closeMenu));
    mobileNav.querySelectorAll('.mnav__links a').forEach(a => a.addEventListener('click', closeMenu));

    document.addEventListener('keydown', (e) => {
      if (!mobileNav.classList.contains('is-open')) return;

      if (e.key === 'Escape') { closeMenu(); return; }

      // Keep tabbing inside the dialog while it is open.
      if (e.key === 'Tab') {
        const items = focusables();
        if (!items.length) return;
        const first = items[0], last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    });
  }

  /* ================================================================
     3. CART DRAWER — AJAX
     ================================================================ */
  const cartDrawer  = document.getElementById('CartDrawer');
  const cartOverlay = document.getElementById('CartOverlay');
  const cartClose   = document.getElementById('CartClose');
  const cartBody    = document.getElementById('CartBody');
  const cartSubtotal = document.getElementById('CartSubtotal');

  const openCart  = () => {
    if (!cartDrawer) return;
    cartDrawer.classList.add('is-open');
    cartOverlay.classList.add('is-visible');
    cartOverlay.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    refreshCart();
  };
  const closeCart = () => {
    if (!cartDrawer) return;
    cartDrawer.classList.remove('is-open');
    cartOverlay.classList.remove('is-visible');
    cartOverlay.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  };

  cartClose?.addEventListener('click', closeCart);
  cartOverlay?.addEventListener('click', closeCart);
  document.querySelectorAll('[data-open-cart]').forEach(btn => btn.addEventListener('click', openCart));

  const formatMoney = (cents) => `$${(cents / 100).toFixed(2)}`;

  const refreshCart = async () => {
    try {
      const res  = await fetch('/cart.js?_fd=0');
      const cart = await res.json();

      // Update cart count badges
      document.querySelectorAll('[data-cart-count]').forEach(el => {
        el.textContent = cart.item_count;
        el.style.display = cart.item_count === 0 ? 'none' : 'flex';
      });

      if (!cartBody) return;

      if (cart.item_count === 0) {
        // is-empty hides the subtotal and checkout button — a checkout
        // button on an empty cart is a dead end.
        cartDrawer.classList.add('is-empty');
        const shopUrl = cartDrawer.dataset.emptyUrl || '/collections/all';
        cartBody.innerHTML = `
          <div class="cart-drawer__empty">
            <p>There are no items in your cart.</p>
            <a href="${shopUrl}" class="btn btn--primary cart-drawer__shop">Start shopping</a>
          </div>`;
        if (cartSubtotal) cartSubtotal.textContent = '';
        return;
      }

      cartDrawer.classList.remove('is-empty');

      cartBody.innerHTML = cart.items.map(item => {
        const propEntries = Object.entries(item.properties || {})
          .filter(([, v]) => v !== '' && v !== null);
        const propsHtml = propEntries.length ? `
            <div style="font-size: 11px; color: #8a8a8a; line-height: 1.5; margin-bottom: 6px; word-break: break-word;">
              ${propEntries.map(([k, v]) => {
                // Property values are typed by customers, so never trust them as HTML.
                const safe = (text) => String(text).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
                const isLink = typeof v === 'string' && /^https?:\/\//.test(v);
                const label = /\/g\/[A-Za-z0-9_-]{22}$/.test(v) ? 'View files' : 'View link';
                return `<div><strong style="color: #bbb;">${safe(k)}:</strong> ${isLink ? `<a href="${safe(v)}" target="_blank" rel="noopener" style="color: var(--color-accent); text-decoration: underline;">${label}</a>` : safe(v)}</div>`;
              }).join('')}
            </div>` : '';

        return `
        <div class="cart-item">
          <div class="cart-item__img">
            <img src="${item.image}" alt="${item.product_title}" width="72" height="72" loading="lazy">
          </div>
          <div style="flex: 1; min-width: 0;">
            <div class="cart-item__title">${item.product_title}</div>
            ${item.variant_title && item.variant_title !== 'Default Title'
              ? `<div class="cart-item__variant">${item.variant_title}</div>`
              : ''}
            ${propsHtml}
            <div class="cart-item__price">×${item.quantity} &nbsp; ${formatMoney(item.final_line_price)}</div>
          </div>
          <button type="button" class="drawer-remove-btn" data-line-key="${item.key}" aria-label="Remove item" style="background: none; border: none; color: #737373; cursor: pointer; padding: 6px; align-self: flex-start; transition: color 0.2s;">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
          </button>
        </div>`;
      }).join('');

      if (cartSubtotal) cartSubtotal.textContent = formatMoney(cart.total_price);
    } catch (err) {
      console.error('Cart fetch error:', err);
    }
  };

  // Delegated trash-button removal (AJAX, no page reload)
  cartBody?.addEventListener('click', async (e) => {
    const btn = e.target.closest('.drawer-remove-btn');
    if (!btn) return;
    btn.disabled = true;
    btn.style.opacity = '0.4';
    try {
      await fetch('/cart/change.js?_fd=0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: btn.dataset.lineKey, quantity: 0 })
      });
      await refreshCart();
    } catch (err) {
      console.error('Cart remove error:', err);
      btn.disabled = false;
      btn.style.opacity = '';
    }
  });

  window.LahazatiCart = { open: openCart, close: closeCart, refresh: refreshCart };

  /* ================================================================
     4. PRODUCT PAGE — TIER SWITCHING, VARIANT, PRICE
     ================================================================ */
  const productJsonEl = document.querySelector('[id^="ProductJson-"]');
  let productData = null;
  if (productJsonEl) {
    try { productData = JSON.parse(productJsonEl.textContent); }
    catch (e) { console.error('Product JSON parse error', e); }
  }

  const tierCards        = document.querySelectorAll('.tier-card');
  const customizerPanels = document.querySelectorAll('.customizer-panel');
  const variantInput     = document.querySelector('[id^="VariantSelector-"]');
  const priceDisplay     = document.querySelector('[id^="ProductPrice-"]');
  const compareDisplay   = document.querySelector('[id^="ComparePrice-"]');
  const stickyPrice      = document.getElementById('StickyATCPrice');
  const stickyTier       = document.getElementById('StickyATCTier');

  const TIER_MAP   = { diy: 0, regular: 1, netflix: 2 };
  const TIER_NAMES = { diy: 'DIY Book', regular: 'Regular Edit', netflix: 'Netflix Edit' };

  const switchTier = (tier) => {
    // Cards
    tierCards.forEach(c => c.classList.toggle('active', c.dataset.tier === tier));

    // Panels
    customizerPanels.forEach(p => {
      const isActive = p.id === `Customizer-${tier.charAt(0).toUpperCase() + tier.slice(1)}`;
      p.classList.toggle('is-active', isActive);
      p.style.display = isActive ? 'block' : 'none';
    });

    // Required field toggles
    document.querySelectorAll('.customizer-panel [data-required]').forEach(input => {
      const inActive = input.closest('.customizer-panel.is-active');
      if (inActive) { input.setAttribute('required', 'required'); }
      else           { input.removeAttribute('required'); }
    });

    // WhatsApp fallback link
    const waLink = document.querySelector('[data-wa-link]');
    if (waLink) {
      const msg = encodeURIComponent(
        `Hi Lahazati, I want to order the ${TIER_NAMES[tier] || tier} and send my files here.`
      );
      waLink.href = `https://wa.me/96181272350?text=${msg}`;
    }

    // Variant + Pricing
    const idx     = TIER_MAP[tier] ?? 0;
    const variant = productData?.variants?.[idx];
    if (variant) {
      if (variantInput) variantInput.value = variant.id;
      const newPrice = formatMoney(variant.price);
      if (priceDisplay) priceDisplay.textContent = newPrice;
      if (stickyPrice)  stickyPrice.textContent  = newPrice;
      if (stickyTier)   stickyTier.textContent   = TIER_NAMES[tier];
      if (compareDisplay) {
        if (variant.compare_at_price > variant.price) {
          compareDisplay.textContent    = formatMoney(variant.compare_at_price);
          compareDisplay.style.display  = 'inline';
        } else {
          compareDisplay.style.display = 'none';
        }
      }
    }
  };

  // Bind clicks
  tierCards.forEach(card => {
    card.addEventListener('click', () => { if (card.dataset.tier) switchTier(card.dataset.tier); });
  });

  // Init on first active card
  const initTier = document.querySelector('.tier-card.active')?.dataset.tier ?? 'diy';
  if (tierCards.length) switchTier(initTier);

  /* ================================================================
     5. GALLERY THUMBNAIL SWITCHER
     ================================================================ */
  const galleryMain   = document.getElementById('GalleryMain');
  const galleryThumbs = document.querySelectorAll('.gallery__thumb');

  galleryThumbs.forEach(thumb => {
    thumb.addEventListener('click', () => {
      galleryThumbs.forEach(t => t.classList.remove('active'));
      thumb.classList.add('active');
      const img = galleryMain?.querySelector('img');
      if (img) {
        img.src = thumb.dataset.full;
        img.alt = thumb.dataset.alt ?? '';
      }
    });
  });

  /* ================================================================
     6. FILE UPLOAD VALIDATION
     ================================================================ */
  const VALID_FORMATS  = ['jpg', 'jpeg', 'png', 'mp4', 'mov', 'heic'];
  const IMAGE_FORMATS  = ['jpg', 'jpeg', 'png', 'heic'];
  const MAX_BYTES      = 100 * 1024 * 1024; // 100 MB

  const validateFile = (file, imageOnly = false) => {
    const ext     = file.name.split('.').pop().toLowerCase();
    const allowed = imageOnly ? IMAGE_FORMATS : VALID_FORMATS;
    if (!allowed.includes(ext)) {
      return `"${file.name}" has an invalid format. Allowed: ${allowed.join(', ')}.`;
    }
    if (file.size > MAX_BYTES) {
      return `"${file.name}" is ${(file.size / 1024 / 1024).toFixed(1)}MB — max is 100MB.`;
    }
    return null;
  };

  const showError = (el, msg) => {
    if (!el) return;
    el.textContent = msg;
    el.classList.add('is-visible');
    clearTimeout(el._timer);
    el._timer = setTimeout(() => el.classList.remove('is-visible'), 6000);
  };

  // DIY optional single file
  const diyInput = document.getElementById('DIYFileInput');
  const diyError = document.getElementById('DIYUploadError');
  diyInput?.addEventListener('change', function () {
    if (!this.files[0]) return;
    const err = validateFile(this.files[0]);
    if (err) { showError(diyError, err); this.value = ''; }
  });

  // Regular Edit dropzone
  initDropzone({
    dropzoneId: 'RegularDropzone',
    inputId:    'RegularFileInput',
    listId:     'RegularFileList',
    errorId:    'RegularUploadError',
    imageOnly:  false
  });

  function initDropzone({ dropzoneId, inputId, listId, errorId, imageOnly }) {
    const zone    = document.getElementById(dropzoneId);
    const input   = document.getElementById(inputId);
    const list    = document.getElementById(listId);
    const errorEl = document.getElementById(errorId);
    if (!zone || !input) return;

    let files = [];

    // Drag events
    ['dragenter','dragover','dragleave','drop'].forEach(ev => {
      zone.addEventListener(ev, e => { e.preventDefault(); e.stopPropagation(); });
    });
    ['dragenter','dragover'].forEach(ev => zone.addEventListener(ev, () => zone.classList.add('dragover')));
    ['dragleave','drop'].forEach(ev => zone.addEventListener(ev, () => zone.classList.remove('dragover')));
    zone.addEventListener('drop', e => processFiles(e.dataTransfer.files));
    input.addEventListener('change', function () { processFiles(this.files); this.value = ''; });

    const processFiles = (rawFiles) => {
      [...rawFiles].forEach(f => {
        const err = validateFile(f, imageOnly);
        if (err) { showError(errorEl, err); return; }
        if (!files.find(x => x.name === f.name && x.size === f.size)) files.push(f);
      });
      renderList();
    };

    const renderList = () => {
      if (!list) return;
      list.innerHTML = files.map((f, i) => `
        <div class="file-item">
          <span class="file-item__name">${f.name}</span>
          <span class="file-item__size">${(f.size / 1024 / 1024).toFixed(1)} MB</span>
          <span class="file-item__remove" data-i="${i}" aria-label="Remove">&times;</span>
        </div>`).join('');
      list.querySelectorAll('.file-item__remove').forEach(btn => {
        btn.addEventListener('click', () => {
          files.splice(parseInt(btn.dataset.i), 1);
          renderList();
        });
      });
    };
  }

  /* ================================================================
     7. AVATAR UPLOAD PREVIEWS (Netflix Edit)
     ================================================================ */
  document.querySelectorAll('.avatar-slot').forEach(slot => {
    const input = slot.querySelector('.avatar-slot__input');
    const label = slot.querySelector('.avatar-slot__label');
    if (!input || !label) return;

    input.addEventListener('change', function () {
      if (!this.files?.[0]) return;
      const err = validateFile(this.files[0], true);
      if (err) { alert(err); this.value = ''; return; }

      const reader = new FileReader();
      reader.onload = e => {
        const existing = label.querySelector('img');
        if (existing) { existing.src = e.target.result; }
        else {
          const img = Object.assign(document.createElement('img'), { src: e.target.result, alt: 'Profile' });
          label.querySelector('.avatar-slot__plus')?.remove();
          label.appendChild(img);
        }
      };
      reader.readAsDataURL(this.files[0]);
    });
  });

  /* ================================================================
     8. AJAX ADD TO CART + FORM CLEANUP
     ================================================================ */
  const productForm = document.querySelector('.product-form');
  if (productForm) {
    productForm.addEventListener('submit', async function (e) {
      e.preventDefault();

      // Disable empty & hidden-panel properties before building FormData
      const propInputs = [...this.querySelectorAll('[name^="properties["]')];
      propInputs.forEach(inp => {
        const inHiddenPanel = inp.closest('.customizer-panel:not(.is-active)');
        const isEmpty = inp.type === 'file'
          ? !inp.files?.length
          : inp.value.trim() === '';
        if (inHiddenPanel || isEmpty) inp.disabled = true;
      });

      const addBtn = this.querySelector('.add-to-cart');
      const btnSpan = addBtn?.querySelector('span') ?? addBtn;
      if (addBtn) { addBtn.disabled = true; if (btnSpan) btnSpan.textContent = 'Adding…'; }

      try {
        const res = await fetch('/cart/add.js?_fd=0', {
          method: 'POST',
          body:   new FormData(this)
        });
        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.description ?? 'Add to cart failed.');
        }
        openCart();
      } catch (err) {
        console.error(err);
        alert(err.message ?? 'Sorry, something went wrong. Please try again.');
      } finally {
        propInputs.forEach(inp => { inp.disabled = false; });
        if (addBtn) { addBtn.disabled = false; if (btnSpan) btnSpan.textContent = 'Add to Cart'; }
      }
    });
  }

  /* ================================================================
     9. STICKY MOBILE ATC
     ================================================================ */
  // Sticky add-to-cart is handled in customizer.js, which knows about the
  // tier variants and the line-item properties. One owner only.

  /* ================================================================
     10. HOW IT WORKS — TAB SWITCHER
     ================================================================ */
  const hiwBtns     = document.querySelectorAll('.hiw-tab-btn');
  const hiwContents = document.querySelectorAll('.hiw-content');

  hiwBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      hiwBtns.forEach(b => { b.classList.remove('active'); b.setAttribute('aria-selected', 'false'); });
      hiwContents.forEach(c => { c.classList.remove('is-active'); c.style.display = 'none'; });
      btn.classList.add('active');
      btn.setAttribute('aria-selected', 'true');
      const target = document.getElementById(btn.dataset.target);
      if (target) { target.classList.add('is-active'); target.style.display = 'block'; }
    });
  });
  // Init default active tab
  const defaultActiveHiw = document.querySelector('.hiw-tab-btn.active') || document.querySelector('.hiw-tab-btn');
  defaultActiveHiw?.click();

  /* ================================================================
     11. FAQ ACCORDION
     ================================================================ */
  document.querySelectorAll('.faq-question').forEach(btn => {
    btn.addEventListener('click', function () {
      const item   = this.closest('.faq-item');
      const answer = item.querySelector('.faq-answer');
      const isOpen = item.classList.contains('is-open');

      // Close all open items
      document.querySelectorAll('.faq-item.is-open').forEach(open => {
        open.classList.remove('is-open');
        open.querySelector('.faq-answer').style.maxHeight = '0';
        open.querySelector('.faq-question').setAttribute('aria-expanded', 'false');
      });

      // Open this one if it was closed
      if (!isOpen) {
        item.classList.add('is-open');
        answer.style.maxHeight = answer.scrollHeight + 'px';
        this.setAttribute('aria-expanded', 'true');
      }
    });
  });

  /* ================================================================
     12. SCROLL REVEAL — INTERSECTION OBSERVER
     ================================================================ */
  const revealEls = document.querySelectorAll('.reveal');
  if (revealEls.length) {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -55px 0px' });
    revealEls.forEach(el => observer.observe(el));
  }

});
